/* eslint-disable no-param-reassign */
/* eslint-disable no-plusplus */
import _ from 'underscore';
import decimal from '@bingads-webui-component-legacy/humanize/decimal';
import targetConstants from '@bingads-webui-campaign-legacy/base-targets/viewmodel/targetConstants';
import { isABWTargetValid, isLengthOfStayTargetValid, isCheckinDateTargetValid } from '../index';
import { isABWOverlapCal } from '../advanced-booking-window-target-setting/utility';
import { isLengthOfStayOverlapCal } from '../length-of-stay-target-setting/utility';
import { isCheckinDateOverlapCal } from '../check-in-date-target-setting/utility';

export function shouldNotHideInclude(bidStrategyType) {
  return bidStrategyType !== 'Commission';
}

const bidAdjustmentConst = {
  min: 0,
  max: 1000,
};
export const numberErrorCode = {
  RequiredField: 'RequiredField',
  InValidNumber: 'InValidNumber',
  OutOfRange: 'OutOfRange',
  RequiredInteger: 'RequiredInteger',
};

export const getNumberErrorMsg = ({
  code, i18n, min, max,
}) => {
  switch (code) {
    case numberErrorCode.RequiredField:
      return i18n.getString(_TL_('Required field'));
    case numberErrorCode.InValidNumber:
      return i18n.getString(_TL_('Field requires numeric value'));
    case numberErrorCode.OutOfRange:
      return i18n.getString(_TL_('Number should be between {{min}} and {{max}}'), { min, max });
    case numberErrorCode.RequiredInteger:
      return i18n.getString(_TL_('Field requires integer value'));
    default:
      return null;
  }
};

export const validateNumber = ({
  value,
  min,
  max,
  isInteger,
  extra,
  onlyExtra,
}) => {
  const isValidNumber = typeof value === 'number' ? true : decimal.isValid(value);
  if (value === '' || value == null) {
    return numberErrorCode.RequiredField;
  } else if (!isValidNumber) {
    return numberErrorCode.InValidNumber;
  }
  const number = decimal.fromLocalToFloat(value);
  if ((number < min || number > max) && ((extra && extra.indexOf(number) === -1) || _.isUndefined(extra))) {
    return numberErrorCode.OutOfRange;
  }
  if (onlyExtra && ((extra && extra.indexOf(number) === -1) || _.isUndefined(extra))) {
    return numberErrorCode.OutOfRange;
  }
  if (isInteger && !Number.isInteger(number)) {
    return numberErrorCode.RequiredInteger;
  }

  return null;
};

const transExtra = (i18n, extra) => {
  const num = extra.length;
  const last = extra[num - 1];
  if (num > 1) {
    const pre = extra.slice(0, num - 1);
    return i18n.getString(_TL_('{{pre}}% or {{last}}%'), { pre: pre.join('%, '), last });
  }
  return i18n.getString(_TL_('{{last}}%'), { last });
};

export const validateBidAdjustment = ({
  value, min = bidAdjustmentConst.min, max = bidAdjustmentConst.max, extra, onlyExtra,
}) => validateNumber({
  value, min, max, extra, onlyExtra, isInteger: true,
});
export const getValidateBidAdjustmentErrorMsg = ({
  code, i18n, min = bidAdjustmentConst.min, max = bidAdjustmentConst.max, extra, onlyExtra,
}) => {
  switch (code) {
    case numberErrorCode.OutOfRange:
      if (extra && extra.length) {
        if (onlyExtra) {
          return i18n.getString(_TL_('The bid adjustment must be {{extra}}'), { extra: transExtra(i18n, extra) });
        }
        return i18n.getString(_TL_('The bid adjustment must be {{extra}} or {{min}}% to {{max}}%'), { min, max, extra: transExtra(i18n, extra) });
      }
      return i18n.getString(_TL_('The bid adjustment must be between {{min}}% and {{max}}%'), { min, max });
    default:
      return getNumberErrorMsg({
        code, i18n, min, max,
      });
  }
};

const defaultSupportedLocationsForGEODeal = ['Country', 'SubGeography', 'MetroArea'];

export const locationErrors = {
  IntentIsNotValid: 'IntentIsNotValid',
  BidIsNotValid: 'BidIsNotValid',
  HasConflict: 'HasConflict',
  InvalidLocationForDeal: 'InvalidLocationForDeal',
};

const checkConflict = (areaLocations) => {
  const includedLocations = [];
  const excludedLocations = [];

  _.each(areaLocations, (location) => {
    if (location.Criterion.LocationCriterion) {
      if (location.SelectionStatus === 'Included') {
        includedLocations.push(location);
      } else if (location.SelectionStatus === 'Excluded') {
        excludedLocations.push(location);
      }
    }
  });

  return _.every(includedLocations, includedLoc =>
    _.every(includedLoc.Criterion.LocationCriterion.EnclosingLocationIds, parentLocationId =>
      _.isUndefined(_.find(excludedLocations, excludedLoc =>
        excludedLoc.Criterion.LocationCriterion.LocationId === parentLocationId))));
};

const locationTypeNameMap = {
  Country: _TL_('country'),
  SubGeography: _TL_('region, state, province '),
  MetroArea: _TL_('metro area'),
  City: _TL_('city'),
  PostalCode: _TL_('postal code'),
  County: _TL_('county'),
  Neighborhood: _TL_('neighborhood'),
};

const constructErrorMessageForDeal = ({ i18n, dealInfo = {} }) => {
  const { dealSupportedLocationType } = dealInfo;
  let message = '';
  if (dealSupportedLocationType) {
    const supportedLocationType = dealSupportedLocationType
      .map(type => (_.has(locationTypeNameMap, type) ? locationTypeNameMap[type] : ''))
      .join(', ');
    message = i18n.getString(_TL_('The location you selected is not valid for this deal. This deal supported location types {{supportedLocationType}} location targeting under the country.'), { supportedLocationType });
  }
  return message;
};

export const getLocationErrorMsg = ({
  code, i18n, dealInfo = {},
}) => {
  switch (code) {
    case locationErrors.HasConflict:
      return i18n.getString(_TL_('You cannot target a location that is within an excluded location.'));
    case locationErrors.InvalidLocationForDeal:
      return constructErrorMessageForDeal({ i18n, dealInfo });
    default:
      return null;
  }
};

export const validateLocations = (location, bidStrategyType, dealInfo = null) => {
  if (location.useParentLevel) {
    return null;
  }
  const {
    currentLevelSettings = {},
  } = location;

  const {
    peopleInLocation,
    peopleSearchingForLocation,
    currentLocations,
  } = currentLevelSettings;

  if (shouldNotHideInclude(bidStrategyType) && !checkConflict(currentLocations)) {
    return locationErrors.HasConflict;
  }

  if (shouldNotHideInclude(bidStrategyType) && (peopleInLocation === false && peopleSearchingForLocation === false)) {
    return locationErrors.IntentIsNotValid;
  }

  const data = currentLocations && currentLocations.find(target => validateBidAdjustment({
    min: -targetConstants.minimumBid.Location,
    max: targetConstants.maximumBid.Location,
    value: target.Criterion.BidAdjustment,
  }) !== null);

  if (data) {
    return locationErrors.BidIsNotValid;
  }

  const {
    isForDeal,
    dealSupportedLocationType,
    dealCountryCode,
  } = dealInfo || {};

  // Deal Check
  if (isForDeal) {
    let invalidCountryLocations = [];
    // If supportedLocationType is not provided, default to country, subgeography, metroarea
    const supportedLocationType = dealSupportedLocationType || defaultSupportedLocationsForGEODeal;
    const invalidTypeLocations = currentLocations
        && currentLocations.find((target) => {
          const locationType = _.get(target, ['Criterion', 'LocationCriterion', 'LocationType'], '');
          return _.contains(supportedLocationType, locationType) === false;
        });

    if (!_.isEmpty(dealCountryCode)) {
      invalidCountryLocations = currentLocations
      && currentLocations.find((target) => {
        const targetCountryCode = _.get(target, ['Criterion', 'LocationCriterion', 'CountryCode'], '').toLowerCase();
        // If the location selected is not in the same country announced by the deal, return error
        return !_.isEqual(dealCountryCode.toLowerCase(), targetCountryCode);
      });
    }

    if (!_.isEmpty(invalidTypeLocations) || !_.isEmpty(invalidCountryLocations)) {
      return locationErrors.InvalidLocationForDeal;
    }
  }

  return null;
};

export const isLocationValid = (location, bidStrategyType, dealInfo = {}) => validateLocations(location, bidStrategyType, dealInfo) === null;

export const getDatePickerStrings = i18n => ({
  months: [
    i18n.getString(_TL_('January')),
    i18n.getString(_TL_('February')),
    i18n.getString(_TL_('March')),
    i18n.getString(_TL_('April')),
    i18n.getString(_TL_('May')),
    i18n.getString(_TL_('June')),
    i18n.getString(_TL_('July')),
    i18n.getString(_TL_('August')),
    i18n.getString(_TL_('September')),
    i18n.getString(_TL_('October')),
    i18n.getString(_TL_('November')),
    i18n.getString(_TL_('December')),
  ],

  shortMonths: [
    i18n.getString(_TL_('Jan')),
    i18n.getString(_TL_('Feb')),
    i18n.getString(_TL_('Mar')),
    i18n.getString(_TL_('Apr')),
    i18n.getString(_TL_('May')),
    i18n.getString(_TL_('Jun')),
    i18n.getString(_TL_('Jul')),
    i18n.getString(_TL_('Aug')),
    i18n.getString(_TL_('Sep')),
    i18n.getString(_TL_('Oct')),
    i18n.getString(_TL_('Nov')),
    i18n.getString(_TL_('Dec')),
  ],

  days: [
    i18n.getString(_TL_('Sunday')),
    i18n.getString(_TL_('Monday')),
    i18n.getString(_TL_('Tuesday')),
    i18n.getString(_TL_('Wednesday')),
    i18n.getString(_TL_('Thursday')),
    i18n.getString(_TL_('Friday')),
    i18n.getString(_TL_('Saturday')),
  ],

  // likezh: These short day initials are not used. Left en-US defaults from Fluent UI.
  // https://github.com/microsoft/fluentui/blob/4f907a837f3b85923178b611e4fbb0d9ac222dda/packages/date-time-utilities/src/dateFormatting/dateFormatting.defaults.ts#L55
  shortDays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],

  goToToday: i18n.getString(_TL_('Go to today')),
  prevMonthAriaLabel: i18n.getString(_TL_('Go to previous month')),
  nextMonthAriaLabel: i18n.getString(_TL_('Go to next month')),
  prevYearAriaLabel: i18n.getString(_TL_('Go to previous year')),
  nextYearAriaLabel: i18n.getString(_TL_('Go to next year')),
  closeButtonAriaLabel: i18n.getString(_TL_('Close date picker')),

  isRequiredErrorMessage: i18n.getString(_TL_('Start date is required.')),
  invalidInputErrorMessage: i18n.getString(_TL_('Invalid date format.')),
});

export const areEqual = (prevProps, nextProps) => prevProps.data === nextProps.data;

export function notifyTargetsShowError(target, patchTarget) {
  if (!target.ABW.useParentLevel && isABWTargetValid(target.ABW.currentLevelSettings) === false) {
    patchTarget('ABW', { ...target.ABW, isInvalid: true });
  }
  if (!target.lengthOfStay.useParentLevel && isLengthOfStayTargetValid(target.lengthOfStay.currentLevelSettings) === false) {
    patchTarget('lengthOfStay', { ...target.lengthOfStay, isInvalid: true });
  }
  if (!target.checkinDate.useParentLevel && isCheckinDateTargetValid(target.checkinDate.currentLevelSettings) === false) {
    patchTarget('checkinDate', { ...target.checkinDate, isInvalid: true });
  }
}

const isValueOverlapMap = {
  ABW: isABWOverlapCal,
  lengthOfStay: isLengthOfStayOverlapCal,
  checkinDate: isCheckinDateOverlapCal,
};
const isValueOverlapMapSortByParams = {
  ABW: { key: 'minimumDays', type: 'number' },
  lengthOfStay: { key: 'minimumNumber', type: 'number' },
  checkinDate: { key: 'startDate', type: 'string' },
};

export function isValueOverlap(targetName, data) {
  if (data && data.length) {
    let sortData = [...data];
    if (isValueOverlapMapSortByParams[targetName] && isValueOverlapMapSortByParams[targetName].type === 'number') {
      sortData = _.sortBy(sortData, item => item[isValueOverlapMapSortByParams[targetName].key] - 0);
    }
    if (isValueOverlapMapSortByParams[targetName] && isValueOverlapMapSortByParams[targetName].type === 'string') {
      sortData = _.sortBy(sortData, item => item[isValueOverlapMapSortByParams[targetName].key]);
    }
    sortData = sortData.filter(item => item.Status !== 'Deleted');
    for (let i = 0, len = sortData.length; i < len; i++) {
      const curDt = sortData[i];
      const isValueOverlapMapFn = isValueOverlapMap[targetName];
      if (sortData.length === 1) {
        if (isValueOverlapMapFn && isValueOverlapMapFn(curDt, {})) {
          return isValueOverlapMapFn(curDt, {});
        }
      }
      for (let j = i + 1; j < len; j++) {
        const otherDt = sortData[j];
        if (isValueOverlapMapFn && isValueOverlapMapFn(curDt, otherDt)) {
          return isValueOverlapMapFn(curDt, otherDt);
        }
      }
    }
  }
  return '';
}
export function setIsDirtyByNotifyFn({ data, setKeySuffix, setIsDirty }) {
  if (data.reset) {
    setKeySuffix(new Date().getTime());
    delete data.reset;
    setIsDirty({});
  }
  if (data.remove) {
    setKeySuffix(new Date().getTime());
    const dirty = {};
    data.currentLevelSettings.forEach((_item, index) => {
      Object.entries(_item).forEach(([key, value]) => {
        if (!data.useParentLevel && value !== '') {
          dirty[`${key}-${index}`] = true;
        }
      });
    });
    setIsDirty(dirty);
    delete data.remove;
  }
  if (data.isInvalid) {
    delete data.isInvalid;
    const dirty = {};
    data.currentLevelSettings.forEach((_item, index) => {
      Object.entries(_item).forEach(([key]) => {
        if (!data.useParentLevel) {
          dirty[`${key}-${index}`] = true;
        }
      });
    });
    setIsDirty(dirty);
  }
}

export const overlapErrorMsg = {
  minimum_greater_than_maximum: _TL_('Minimum days cannot be greater than maximum days'),
  minimum_greater_than_maximum_nights: _TL_('Minimum number of nights cannot be greater than maximum number of nights'),
  adjust_settings: _TL_("Adjust your settings so they don't overlap."),
  startdate_greater_than_enddate: _TL_('Start check-in date cannot be later than end check-in date'),
  out_of_checkin_date_range: _TL_('The number of days between start check-in date and end check-in date cannot be greater than 999.'),
};
